import { useCallback, useMemo } from "react";
import parse, { HTMLReactParserOptions } from "html-react-parser";
import _ from "lodash";

import { colors } from "theme/palette";

import Box from "components/Box";
import Stack from "components/Stack";
import Text from "components/Text";

import PrintableFieldTitle from "pages/PrintableChecklist/components/PrintableFieldTitle";

import FieldValue from "entities/Field/components/FormTextField/FieldValue";
import { FIELD_PLACEHOLDER_REGEX } from "entities/Field/components/FormTextField/FormText/constants";
import { getFieldIdFromPlaceholder } from "entities/Field/components/FormTextField/FormText/utils";
import { EFieldStatus } from "entities/Field/constants";
import { IField } from "entities/Field/sdk";

interface IPrintableFormTextField {
  field: IField;
}

const PrintableFormTextField = ({ field }: IPrintableFormTextField) => {
  const parseAndReplaceWithComponents = useCallback(
    ({ text }: { text: string }) => {
      let lastIndex = 0;
      const components: React.ReactNode[] = [];

      let match = FIELD_PLACEHOLDER_REGEX.exec(text);

      while (match !== null) {
        const placeholder = match[0];
        const beforeText = text.slice(lastIndex, match.index);
        components.push(beforeText);

        const fieldId = getFieldIdFromPlaceholder({ placeholder });
        const fieldTemplateMeta = _.find(
          field.template_meta,
          (field) => field.field_id === fieldId
        );

        if (_.isUndefined(fieldTemplateMeta)) {
          components.push(
            <Text fontWeight="bold" display="inline-block">
              Missing field
            </Text>
          );
        } else {
          components.push(<FieldValue templateMeta={fieldTemplateMeta} />);
        }

        lastIndex = match.index + match[0].length;
        match = FIELD_PLACEHOLDER_REGEX.exec(text);
      }

      const remainingText = text.slice(lastIndex);
      components.push(remainingText);

      return components;
    },
    [field.template_meta]
  );

  const advancedParse = useCallback(
    ({ htmlString }: { htmlString: string }) => {
      const options = {
        replace: ({ data }: any) => {
          if (!_.isNil(data) && data !== "") {
            return <>{parseAndReplaceWithComponents({ text: data })}</>;
          }
        },
      } as HTMLReactParserOptions;

      return parse(htmlString, options);
    },
    [parseAndReplaceWithComponents]
  );

  const toRender = useMemo(
    () =>
      advancedParse({
        htmlString: !_.isNil(field.template) ? field.template : "",
      }),
    [field.template, advancedParse]
  );

  return (
    <>
      <Stack
        id={field.field_id.toString()}
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={2}
        sx={{
          padding: 0.5,
          borderBottom: `1px solid ${colors.blue20}`,
          backgroundColor: "initial",
        }}
      >
        <PrintableFieldTitle field={field} />
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          paddingTop={0.5}
          sx={{ flexGrow: 0 }}
        >
          {field.status === EFieldStatus.NA && (
            <Text sx={{ color: colors.gray60 }}>N/A</Text>
          )}
        </Stack>
      </Stack>
      <Box sx={{ marginY: 3 }}>
        <Text>{toRender}</Text>
      </Box>
    </>
  );
};

export default PrintableFormTextField;
